/**
 * Session API service
 */

import apiClient from './api';

export interface SessionResponse {
  id: string;
  name: string;
  user_id: string;
  parent_session_id: string | null;
  is_active: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SessionListResponse {
  sessions: SessionResponse[];
  total: number;
}

export const sessionService = {
  /**
   * Create a new session (optionally with a name)
   */
  async createSession(name?: string): Promise<any> {
    const payload: { name: string; parent_session_id?: string } = {
      name: name ?? 'New Session',
    };
    const response = await apiClient.post('/api/sessions', payload);
    return response.data;
  },

  /**
   * Get session by ID
   */
  async getSession(sessionId: string): Promise<any> {
    const response = await apiClient.get(`/api/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * List all sessions for the authenticated user
   */
  async listSessions(includeCompleted?: boolean): Promise<any> {
    const params: Record<string, string> = {};
    if (includeCompleted !== undefined) {
      params['include_completed'] = String(includeCompleted);
    }
    const response = await apiClient.get('/api/sessions', { params });
    return response.data;
  },

  /**
   * Rename an existing session
   */
  async updateSession(sessionId: string, name: string): Promise<any> {
    const response = await apiClient.patch(`/api/sessions/${sessionId}`, { name });
    return response.data;
  },

  /**
   * Mark a session as complete
   */
  async completeSession(sessionId: string): Promise<any> {
    const response = await apiClient.post(`/api/sessions/${sessionId}/complete`);
    return response.data;
  },

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<any> {
    const response = await apiClient.delete(`/api/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * List all versions (child sessions) of a session
   */
  async getVersions(sessionId: string): Promise<any> {
    const response = await apiClient.get(`/api/sessions/${sessionId}/versions`);
    return response.data;
  },

  /**
   * Create a new version of an existing session
   */
  async createVersion(parentSessionId: string, versionName: string): Promise<any> {
    const response = await apiClient.post('/api/sessions', {
      name: versionName,
      parent_session_id: parentSessionId,
    });
    return response.data;
  },
};

export default sessionService;
